/**
 * TrichoCameraPlugin — interfaccia astratta della tricocamera WiFi.
 *
 * ┌────────────────────────────────────────────────────────────────────┐
 * │  L'INTERFACCIA È UNICA. L'IMPLEMENTAZIONE CAMBIA IN BASE ALL'AMBIENTE. │
 * └────────────────────────────────────────────────────────────────────┘
 *
 *   • In preview web → mock implementato con webcam del browser
 *     (getUserMedia). Serve per validare la UX del flusso a 3 viste
 *     senza tricocamera fisica.
 *
 *   • In app Capacitor nativa (iOS/Android, futuro) → plugin nativo
 *     TrichoCameraPlugin (Swift/Kotlin) che parla TCP con la camera reale
 *     usando la sequenza documentata in /app/PLUGIN_HANDOFF.md.
 *
 * L'app React vede SEMPRE la stessa API:
 *   connect()      → Promise<{ status: "connected" | "failed", error? }>
 *   startLive(el)  → Promise<void>   (mostra il video in un elemento HTML)
 *   capture()      → Promise<{ path, width, height, mimeType, sizeBytes, dataUrl }>
 *   disconnect()   → Promise<void>
 *   addListener(event, cb)
 *   removeAllListeners()
 *
 * Il chiamante riceve solo path JPEG (o dataUrl base64 nel mock). Mai
 * conosce TCP, handshake, HEVC.
 */

import { isCapacitorNative } from "./featureFlags";

// ─────────────────────────────────────────────────────────────────────
// MOCK WEBCAM — gira nel browser, simula il flusso live + scatta.
// In produzione nativa questo mock viene by-passato.
// ─────────────────────────────────────────────────────────────────────
function createWebcamMock() {
  let mediaStream = null;
  let videoEl = null;
  const listeners = { statusChange: [], error: [] };

  const emit = (event, data) => {
    (listeners[event] || []).forEach((cb) => {
      try { cb(data); } catch (e) { /* ignore */ }
    });
  };

  async function connect() {
    emit("statusChange", { status: "connecting", message: "Attivazione webcam (mock)…" });
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const err = "Webcam non disponibile in questo browser";
      emit("error", { code: "no_webcam", message: err });
      return { status: "failed", error: err };
    }
    try {
      // Risoluzione coerente con la tricocamera reale (1920x1080) quando
      // possibile, fallback automatico se la webcam non la supporta.
      mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width:  { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: "user",
        },
        audio: false,
      });
      emit("statusChange", { status: "connected", message: "Webcam connessa (mock)" });
      return { status: "connected" };
    } catch (e) {
      const err = e?.message || "Permesso webcam negato";
      emit("error", { code: "permission_denied", message: err });
      return { status: "failed", error: err };
    }
  }

  async function startLive(containerEl) {
    if (!mediaStream) throw new Error("Non connesso");
    if (!containerEl) throw new Error("containerEl mancante");

    // Pulisci eventuali video precedenti
    while (containerEl.firstChild) containerEl.removeChild(containerEl.firstChild);

    videoEl = document.createElement("video");
    videoEl.autoplay = true;
    videoEl.playsInline = true;
    videoEl.muted = true;
    videoEl.style.width = "100%";
    videoEl.style.height = "100%";
    videoEl.style.objectFit = "cover";
    videoEl.style.background = "#000";
    videoEl.srcObject = mediaStream;

    containerEl.appendChild(videoEl);
    try { await videoEl.play(); } catch (e) { /* autoplay potrebbe richiedere user gesture */ }
  }

  async function capture() {
    if (!videoEl || !mediaStream) throw new Error("Live non attivo");
    const track = mediaStream.getVideoTracks?.()[0];
    const settings = track?.getSettings?.() || {};
    const width  = settings.width  || videoEl.videoWidth  || 1280;
    const height = settings.height || videoEl.videoHeight || 720;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoEl, 0, 0, width, height);

    const dataUrl  = canvas.toDataURL("image/jpeg", 0.92);
    const sizeBytes = Math.floor(dataUrl.length * 3 / 4);

    return {
      // path è una pseudo-uri stringa, sul nativo sarà un path reale del sandbox
      path: `mock://capture/${Date.now()}.jpg`,
      width, height,
      mimeType: "image/jpeg",
      sizeBytes,
      dataUrl,            // l'app legge questo per il preview
    };
  }

  async function disconnect() {
    try {
      if (videoEl) {
        videoEl.pause();
        videoEl.srcObject = null;
        if (videoEl.parentNode) videoEl.parentNode.removeChild(videoEl);
      }
      if (mediaStream) {
        mediaStream.getTracks().forEach((t) => t.stop());
      }
    } finally {
      videoEl = null;
      mediaStream = null;
      emit("statusChange", { status: "disconnected" });
    }
  }

  function addListener(event, cb) {
    if (!listeners[event]) listeners[event] = [];
    listeners[event].push(cb);
    return { remove: () => {
      listeners[event] = (listeners[event] || []).filter((x) => x !== cb);
    }};
  }

  function removeAllListeners() {
    Object.keys(listeners).forEach((k) => { listeners[k] = []; });
  }

  return { connect, startLive, capture, disconnect, addListener, removeAllListeners, _isMock: true };
}

// ─────────────────────────────────────────────────────────────────────
// PLUGIN REALE (futuro, in app Capacitor nativa)
// ─────────────────────────────────────────────────────────────────────
function createNativePlugin() {
  // Questa funzione esiste come placeholder. Quando il plugin Capacitor
  // verrà installato:
  //   yarn add @aitricoprotocol/capacitor-tricho-camera
  //   npx cap sync ios
  //
  // si sostituirà il corpo con:
  //   import { TrichoCamera } from "@aitricoprotocol/capacitor-tricho-camera";
  //   return TrichoCamera;
  //
  // Per ora, se siamo "nativi" ma il plugin reale non è installato,
  // restituiamo un errore esplicito.
  return {
    async connect() {
      return {
        status: "failed",
        error: "Plugin nativo non installato. Vedere PLUGIN_HANDOFF.md.",
      };
    },
    async startLive() { throw new Error("Plugin nativo non installato"); },
    async capture()   { throw new Error("Plugin nativo non installato"); },
    async disconnect() { /* no-op */ },
    addListener() { return { remove() {} }; },
    removeAllListeners() {},
    _isMock: false,
    _isStub: true,
  };
}

// ─────────────────────────────────────────────────────────────────────
// FACTORY: ritorna l'implementazione corretta in base all'ambiente.
// ─────────────────────────────────────────────────────────────────────
let _instance = null;
export function getTrichoCameraPlugin() {
  if (_instance) return _instance;
  _instance = isCapacitorNative() ? createNativePlugin() : createWebcamMock();
  return _instance;
}

/**
 * Etichetta umana dell'implementazione attiva. Utile per UI di debug
 * ("Mock webcam" vs "Tricocamera nativa") e per i test.
 */
export function getTrichoCameraImplLabel() {
  const p = getTrichoCameraPlugin();
  if (p._isStub) return "Plugin nativo (stub — non installato)";
  if (p._isMock) return "Mock webcam browser (per validazione UX)";
  return "Tricocamera nativa";
}
